import { useRef, useState, useEffect } from 'react';
import { Box, IconButton } from '@mui/joy';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ShowCard from './ShowCard';
import { useCurrentTheme } from '../hooks/useCurrentTheme';

function CardSlider({ cards, token }) {
    const scrollRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const theme = useCurrentTheme();

    const updateScrollButtons = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
    };

    const scrollToIndex = (index) => {
        const el = scrollRef.current;
        if (!el) return;
        const card = el.children[index];
        if (card) {
            el.scrollTo({
                left: card.offsetLeft,
                behavior: 'smooth'
            });
            setActiveIndex(index);
        }
    };

    const scroll = (direction) => {
        const newIndex = direction === 'left'
            ? Math.max(activeIndex - 1, 0)
            : Math.min(activeIndex + 1, cards.length - 1);
        scrollToIndex(newIndex);
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            updateScrollButtons();
            const children = Array.from(el.children);
            const scrollLeft = el.scrollLeft;
            const center = scrollLeft + el.clientWidth / 2;

            let closestIndex = 0;
            let closestDistance = Infinity;
            children.forEach((child, index) => {
                const childCenter = child.offsetLeft + child.clientWidth / 2;
                const distance = Math.abs(center - childCenter);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });
            setActiveIndex(closestIndex);
        };

        updateScrollButtons();
        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [cards.length]);

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Влево */}
            <IconButton
                variant="plain"
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    borderRadius: '50px',
                    left: { xs: 8, sm: -32 },
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    '&:hover': {
                        backgroundColor: theme.chevron.bgHover + "!important"
                    },
                    '&:active': {
                        backgroundColor: theme.chevron.bgActive + "!important"
                    }
                }}
            >
                <ChevronLeft
                    sx={{
                        fill: !canScrollLeft ? theme.chevron.disabled : theme.chevron.active
                    }}
                />
            </IconButton>

            {/* Слайдер */}
            <Box
                ref={scrollRef}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollBehavior: 'smooth',
                    gap: 2,
                    py: 2,
                    px: 1,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {cards.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: '0 0 100%',
                            scrollSnapAlign: 'center',
                            maxWidth: '100%',
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <ShowCard item={item} index={index} token={token} />
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Вправо */}
            <IconButton
                variant="plain"
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    borderRadius: '50px',
                    right: { xs: 8, sm: -32 },
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    '&:hover': {
                        backgroundColor: theme.chevron.bgHover + "!important"
                    },
                    '&:active': {
                        backgroundColor: theme.chevron.bgActive + "!important"
                    }
                }}
            >
                <ChevronRight
                    sx={{
                        fill: !canScrollRight ? theme.chevron.disabled : theme.chevron.active
                    }}
                />
            </IconButton>

            {/* Точки */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 2,
                }}
            >
                {cards.map((_, index) => (
                    <Box
                        key={index}
                        onClick={() => scrollToIndex(index)}
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: index === activeIndex ? 'primary.solidBg' : theme.dots.unactive,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease-in-out',
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default CardSlider;
